import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { subUserAPI, propertyAPI, roomAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  UserPlus, Users, Trash2, Pencil, Shield, Building, Bed,
  Loader2, Mail, Phone, Eye, EyeOff, ChevronDown, ChevronRight,
  Check, X
} from 'lucide-react';

export default function UserManagementPage() {
  const { user } = useAuth();
  const [subUsers, setSubUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [roomsByProperty, setRoomsByProperty] = useState({});
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Form state
  const [form, setForm] = useState({
    email: '', password: '', nombre: '', telefono: '',
    role: 'propietario', property_permissions: [], module_permissions: null
  });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedProps, setExpandedProps] = useState({});

  const ALL_MODULES = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'calendario', label: 'Calendario' },
    { key: 'multi_calendario', label: 'Multi-Calendario (vista de todas las propiedades)' },
    { key: 'reservas', label: 'Reservas' },
    { key: 'habitaciones', label: 'Habitaciones' },
    { key: 'ingresos_egresos', label: 'Ingresos / Egresos' },
    { key: 'productos_servicios', label: 'Productos y Servicios' },
    { key: 'informes', label: 'Informes' },
    { key: 'auditorias', label: 'Auditorías' },
    { key: 'ajustes', label: 'Ajustes' },
    { key: 'conexiones_ota', label: 'Conexiones OTA' },
    { key: 'simulador_tarifas', label: 'Simulador Tarifas' },
    { key: 'facturacion', label: 'Facturación' },
    { key: 'usuario_curso', label: 'Usuario en curso' },
  ];

  useEffect(() => {
    if (user?.role !== 'propietario' && user?.role !== 'limitado') {
      loadData();
    }
  }, [user]);

  // Restrict sub-users from this page
  if (user?.role === 'propietario' || user?.role === 'limitado') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">Acceso restringido</p>
            <p className="text-sm text-gray-400 mt-1">Solo administradores pueden gestionar usuarios</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, propsRes] = await Promise.all([
        subUserAPI.getAll(),
        propertyAPI.getAll()
      ]);
      setSubUsers(usersRes.data);
      setProperties(propsRes.data);
      // Load rooms for all properties
      const roomsMap = {};
      await Promise.all(propsRes.data.map(async (p) => {
        try {
          const rRes = await roomAPI.getAll(p.id);
          roomsMap[p.id] = rRes.data;
        } catch { roomsMap[p.id] = []; }
      }));
      setRoomsByProperty(roomsMap);
    } catch { toast.error('Error cargando datos'); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingUser(null);
    setForm({ email: '', password: '', nombre: '', telefono: '', role: 'propietario', property_permissions: [], module_permissions: ALL_MODULES.map(m => m.key) });
    setExpandedProps({});
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email, password: '', nombre: u.nombre, telefono: u.telefono || '',
      role: u.role, property_permissions: u.property_permissions || [],
      module_permissions: u.module_permissions || ALL_MODULES.map(m => m.key)
    });
    const expanded = {};
    (u.property_permissions || []).forEach(pp => { expanded[pp.property_id] = true; });
    setExpandedProps(expanded);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.email.trim()) {
      toast.error('Nombre y email son requeridos');
      return;
    }
    if (!editingUser && !form.password) {
      toast.error('La contraseña es requerida para nuevos usuarios');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = {
          nombre: form.nombre, telefono: form.telefono, email: form.email,
          role: form.role, property_permissions: form.property_permissions,
          module_permissions: form.module_permissions
        };
        if (form.password) updateData.password = form.password;
        await subUserAPI.update(editingUser.id, updateData);
        toast.success('Usuario actualizado');
      } else {
        await subUserAPI.create({ ...form });
        toast.success('Usuario creado exitosamente');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await subUserAPI.delete(deleteTarget.id);
      toast.success(`Usuario "${deleteTarget.nombre}" eliminado`);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch { toast.error('Error al eliminar'); }
  };

  // Permission helpers
  const isPropertySelected = (propId) => form.property_permissions.some(pp => pp.property_id === propId);

  const toggleProperty = (propId) => {
    if (isPropertySelected(propId)) {
      setForm(prev => ({
        ...prev,
        property_permissions: prev.property_permissions.filter(pp => pp.property_id !== propId)
      }));
    } else {
      setForm(prev => ({
        ...prev,
        property_permissions: [...prev.property_permissions, { property_id: propId, room_ids: 'all' }]
      }));
    }
  };

  const getPermission = (propId) => form.property_permissions.find(pp => pp.property_id === propId);

  const setRoomAccess = (propId, type) => {
    setForm(prev => ({
      ...prev,
      property_permissions: prev.property_permissions.map(pp =>
        pp.property_id === propId ? { ...pp, room_ids: type === 'all' ? 'all' : [] } : pp
      )
    }));
  };

  const toggleRoom = (propId, roomId) => {
    setForm(prev => ({
      ...prev,
      property_permissions: prev.property_permissions.map(pp => {
        if (pp.property_id !== propId) return pp;
        const current = Array.isArray(pp.room_ids) ? pp.room_ids : [];
        const newIds = current.includes(roomId) ? current.filter(r => r !== roomId) : [...current, roomId];
        return { ...pp, room_ids: newIds };
      })
    }));
  };

  const getPropertyName = (propId) => properties.find(p => p.id === propId)?.nombre || propId;
  const getRoomName = (propId, roomId) => (roomsByProperty[propId] || []).find(r => r.id === roomId)?.nombre || roomId;

  const getRoleBadge = (role) => {
    if (role === 'propietario') return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Propietario</Badge>;
    return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Limitado</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="user-management-title">
              <Users className="h-6 w-6 text-amber-600" />
              Gestión de Usuarios
            </h1>
            <p className="text-muted-foreground mt-1">
              Crea y administra usuarios con acceso a propiedades y habitaciones específicas
            </p>
          </div>
          <Button onClick={openCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="create-user-btn">
            <UserPlus className="h-4 w-4 mr-2" />
            Crear Usuario
          </Button>
        </div>

        {/* Users List */}
        {subUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No hay usuarios secundarios creados</p>
              <p className="text-sm text-gray-400 mt-1">Crea un usuario para asignarle propiedades y habitaciones</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {subUsers.map(u => (
              <Card key={u.id} data-testid={`user-card-${u.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-lg truncate">{u.nombre}</span>
                        {getRoleBadge(u.role)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{u.email}</span>
                        {u.telefono && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{u.telefono}</span>}
                      </div>
                      {/* Show permissions summary */}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(u.property_permissions || []).map(pp => (
                          <Badge key={pp.property_id} variant="outline" className="text-xs">
                            <Building className="h-3 w-3 mr-1" />
                            {getPropertyName(pp.property_id)}
                            {pp.room_ids !== 'all' && Array.isArray(pp.room_ids) && (
                              <span className="ml-1 text-amber-600">({pp.room_ids.length} hab.)</span>
                            )}
                          </Badge>
                        ))}
                        {(!u.property_permissions || u.property_permissions.length === 0) && (
                          <span className="text-xs text-red-500">Sin propiedades asignadas</span>
                        )}
                      </div>
                      {/* Module permissions summary */}
                      {u.module_permissions && Array.isArray(u.module_permissions) && (
                        <div className="mt-1.5">
                          <span className="text-xs text-muted-foreground">{u.module_permissions.length} módulo{u.module_permissions.length !== 1 ? 's' : ''} activo{u.module_permissions.length !== 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700 hover:border-red-300"
                        onClick={() => { setDeleteTarget(u); setDeleteDialogOpen(true); }}
                        data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingUser ? <Pencil className="h-5 w-5 text-amber-600" /> : <UserPlus className="h-5 w-5 text-amber-600" />}
                {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nombre completo *</Label>
                  <Input value={form.nombre} onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                    placeholder="Ej: María López" data-testid="user-form-nombre" />
                </div>
                <div className="space-y-1.5">
                  <Label>Correo electrónico *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    placeholder="maria@email.com" data-testid="user-form-email" />
                </div>
                <div className="space-y-1.5">
                  <Label>{editingUser ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      placeholder="********" data-testid="user-form-password" />
                    <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                    placeholder="3001234567" data-testid="user-form-telefono" />
                </div>
              </div>

              {/* Role */}
              <div className="space-y-1.5">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="user-form-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="propietario">Propietario</SelectItem>
                    <SelectItem value="limitado">Usuario Limitado</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.role === 'propietario' ? 'Puede ver informes y datos de las propiedades/habitaciones asignadas' : 'Acceso de solo lectura a las propiedades/habitaciones asignadas'}
                </p>
              </div>

              {/* Property & Room Permissions */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-600" />
                  Permisos de Acceso
                </Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  Selecciona las propiedades y habitaciones a las que tendrá acceso
                </p>

                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto" data-testid="permissions-list">
                  {properties.map(prop => {
                    const selected = isPropertySelected(prop.id);
                    const perm = getPermission(prop.id);
                    const rooms = roomsByProperty[prop.id] || [];
                    const isExpanded = expandedProps[prop.id];

                    return (
                      <div key={prop.id} className="bg-white">
                        <div className="flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleProperty(prop.id)} data-testid={`perm-prop-${prop.id}`}>
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'bg-amber-600 border-amber-600' : 'border-gray-300'}`}>
                            {selected && <Check className="h-3.5 w-3.5 text-white" />}
                          </div>
                          <Building className="h-4 w-4 text-gray-500" />
                          <span className="flex-1 text-sm font-medium">{prop.nombre}</span>
                          <Badge variant="outline" className="text-xs">{rooms.length} hab.</Badge>
                          {selected && rooms.length > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); setExpandedProps(p => ({ ...p, [prop.id]: !p[prop.id] })); }}
                              className="p-0.5 hover:bg-gray-200 rounded" data-testid={`expand-prop-${prop.id}`}>
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                        </div>

                        {/* Room selection */}
                        {selected && isExpanded && rooms.length > 0 && (
                          <div className="pl-10 pr-3 pb-3 space-y-2 bg-gray-50/50">
                            <div className="flex items-center gap-3 pt-1">
                              <button onClick={() => setRoomAccess(prop.id, 'all')}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${perm?.room_ids === 'all' ? 'bg-amber-100 border-amber-400 text-amber-800' : 'border-gray-300 hover:border-amber-300'}`}
                                data-testid={`rooms-all-${prop.id}`}>
                                Todas las habitaciones
                              </button>
                              <button onClick={() => setRoomAccess(prop.id, 'specific')}
                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${perm?.room_ids !== 'all' ? 'bg-amber-100 border-amber-400 text-amber-800' : 'border-gray-300 hover:border-amber-300'}`}
                                data-testid={`rooms-specific-${prop.id}`}>
                                Seleccionar específicas
                              </button>
                            </div>
                            {perm?.room_ids !== 'all' && (
                              <div className="grid grid-cols-2 gap-1.5">
                                {rooms.map(room => {
                                  const roomSelected = Array.isArray(perm?.room_ids) && perm.room_ids.includes(room.id);
                                  return (
                                    <button key={room.id} onClick={() => toggleRoom(prop.id, room.id)}
                                      className={`flex items-center gap-2 text-xs px-2.5 py-2 rounded-lg border text-left transition-colors ${roomSelected ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-gray-200 hover:border-blue-200'}`}
                                      data-testid={`room-perm-${room.id}`}>
                                      <Bed className={`h-3.5 w-3.5 ${roomSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                                      <span className="flex-1 truncate">{room.nombre}</span>
                                      {roomSelected && <Check className="h-3.5 w-3.5 text-blue-600 shrink-0" />}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Module Visibility Toggles */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-amber-600" />
                  Módulos Visibles
                </Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  Activa o desactiva los módulos que este usuario podrá ver en su panel
                </p>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto" data-testid="module-permissions-list">
                  {ALL_MODULES.map(mod => {
                    const enabled = (form.module_permissions || []).includes(mod.key);
                    return (
                      <div key={mod.key} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          const current = form.module_permissions || [];
                          const updated = enabled ? current.filter(k => k !== mod.key) : [...current, mod.key];
                          setForm(p => ({ ...p, module_permissions: updated }));
                        }}
                        data-testid={`module-toggle-${mod.key}`}>
                        <span className="text-sm">{mod.label}</span>
                        <div className={`w-10 h-5 rounded-full transition-colors relative ${enabled ? 'bg-amber-500' : 'bg-gray-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button type="button" className="text-xs text-amber-600 hover:underline" onClick={() => setForm(p => ({ ...p, module_permissions: ALL_MODULES.map(m => m.key) }))} data-testid="enable-all-modules">Activar todos</button>
                  <span className="text-xs text-gray-300">|</span>
                  <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => setForm(p => ({ ...p, module_permissions: [] }))} data-testid="disable-all-modules">Desactivar todos</button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="user-form-cancel">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="user-form-save">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Usuario</AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de eliminar a <strong>{deleteTarget?.nombre}</strong>? Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-user-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-user-confirm">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
